// src/pages/courierInvoice/CourierInvoiceListPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Plus,
  Eye,
  RefreshCw,
  Trash2,
  X,
  AlertCircle,
  Truck
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import {
  getAllInvoices,
  cancelInvoice,
  deleteInvoice
} from '../../services/api/courierInvoice';
import {
  getBusinessCouriers
} from '../../services/api/businessCourier';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate } from '../../utils/dateUtils';
import styles from './CourierInvoiceListPage.module.css';

const CourierInvoiceListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // Data state
  const [invoices, setInvoices] = useState([]);
  const [businessCouriers, setBusinessCouriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    businessCourierId: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Bulk actions
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    type: '',
    invoiceId: null,
    invoiceNumber: ''
  });

  const statusColors = {
    pending: '#f59e0b',
    processing: '#3b82f6',
    completed: '#10b981',
    failed: '#ef4444',
    partially_processed: '#f97316'
  };

  // Load data on mount
  useEffect(() => {
    loadBusinessCouriers();
  }, []);

  // Load invoices when filters or pagination changes
  useEffect(() => {
    loadInvoices();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  const loadBusinessCouriers = async () => {
    try {
      const response = await getBusinessCouriers();
      setBusinessCouriers(response.couriers || []);
    } catch (error) {
      console.error('Failed to load business couriers:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection,
        ...filters
      };

      const response = await getAllInvoices(params);
      setInvoices(response.invoices || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));
      setSelectedInvoices([]);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      showError(t('courierInvoice.failedToLoadInvoices'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      businessCourierId: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleCancelInvoice = async (invoiceId) => {
    try {
      await cancelInvoice(invoiceId);
      success(t('courierInvoice.invoiceCancelledSuccess'));
      loadInvoices();
      setConfirmModal({ show: false, type: '', invoiceId: null, invoiceNumber: '' });
    } catch (error) {
      console.error('Failed to cancel invoice:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToCancel'));
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await deleteInvoice(invoiceId);
      success(t('courierInvoice.invoiceDeletedSuccess'));
      loadInvoices();
      setConfirmModal({ show: false, type: '', invoiceId: null, invoiceNumber: '' });
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToDelete'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedInvoices.length === 0) return;

    const confirmed = window.confirm(
      t('courierInvoice.confirmBulkDelete', { count: selectedInvoices.length })
    );

    if (!confirmed) return;

    try {
      let successCount = 0;
      let failCount = 0;

      for (const invoiceId of selectedInvoices) {
        try {
          await deleteInvoice(invoiceId);
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        success(t('courierInvoice.bulkDeleteSuccess', { count: successCount }));
      }
      if (failCount > 0) {
        showError(t('courierInvoice.bulkDeleteFailed', { count: failCount }));
      }

      loadInvoices();
    } catch (error) {
      showError(t('courierInvoice.failedToBulkDelete'));
    }
  };

  const handleExport = () => {
    const headers = [
      t('courierInvoice.invoiceNumberCol'),
      t('courierInvoice.courierCompany'),
      t('courierInvoice.invoiceDateCol'),
      t('courierInvoice.totalOrdersCol'),
      t('courierInvoice.statusCol'),
      t('courierInvoice.amountPayable'),
      t('courierInvoice.uploadedBy'),
      t('courierInvoice.uploadedOn')
    ];
    
    const rows = invoices.map(inv => [
      inv.invoiceNumber,
      inv.courierAccount?.CourierCompany?.name || t('common.na'),
      inv.invoiceDate,
      inv.totalOrders,
      t(`courierInvoice.${inv.status}`),
      inv.totalAmountPayable,
      inv.uploadedBy?.name || t('common.na'),
      formatDate(inv.createdAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `courier-invoices-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const canCancel = (invoice) => {
    return ['pending', 'processing'].includes(invoice.status);
  };

  const canDelete = (invoice) => {
    return ['failed', 'pending'].includes(invoice.status);
  };

  const canReprocess = (invoice) => {
    return invoice.status === 'partially_processed';
  };

  // Define filter configuration
  const filterDefinitions = [
    {
      key: 'businessCourierId',
      label: t('courierInvoice.courierAccount'),
      type: 'select',
      icon: <Truck size={18} />,
      placeholder: t('courierInvoice.allCouriers'),
      options: businessCouriers.map(courier => ({
        value: courier.id.toString(),
        label: `${courier.accountName} (${courier.CourierCompany?.name})`
      }))
    },
    {
      key: 'status',
      label: t('courierInvoice.statusCol'),
      type: 'select',
      placeholder: t('courierInvoice.allStatuses'),
      options: [
        { value: 'pending', label: t('courierInvoice.pending') },
        { value: 'processing', label: t('courierInvoice.processing') },
        { value: 'completed', label: t('courierInvoice.completed') },
        { value: 'failed', label: t('courierInvoice.failed') },
        { value: 'partially_processed', label: t('courierInvoice.partiallyProcessed') }
      ]
    },
    {
      key: 'date',
      label: t('courierInvoice.invoiceDateCol'),
      type: 'daterange',
      gridColumn: 'span 2'
    }
  ];

  // Define table columns
  const columns = [
    {
      key: 'invoiceNumber',
      header: t('courierInvoice.invoiceNumberCol'),
      sortable: true,
      width: '150px',
      render: (row) => (
        <span className={styles.invoiceNumber}>{row.invoiceNumber}</span>
      )
    },
    {
      key: 'courier',
      header: t('courierInvoice.courierCompany'),
      width: '200px',
      render: (row) => (
        <div className={styles.courierCell}>
          <span className={styles.courierName}>
            {row.courierAccount?.CourierCompany?.name || t('common.na')}
          </span>
          <span className={styles.accountName}>
            {row.courierAccount?.accountName}
          </span>
        </div>
      )
    },
    {
      key: 'invoiceDate',
      header: t('courierInvoice.invoiceDateCol'),
      sortable: true,
      width: '120px',
      render: (row) => row.invoiceDate
    },
    {
      key: 'totalOrders',
      header: t('courierInvoice.totalOrdersCol'),
      sortable: true,
      width: '100px',
      align: 'center'
    },
    {
      key: 'status',
      header: t('courierInvoice.statusCol'),
      width: '140px',
      render: (row) => (
        <span
          className={styles.statusBadge}
          style={{
            backgroundColor: `${statusColors[row.status]}20`,
            color: statusColors[row.status]
          }}
        >
          {t(`courierInvoice.${row.status}`)}
        </span>
      )
    },
    {
      key: 'totalAmountPayable',
      header: t('courierInvoice.amountPayable'),
      sortable: true,
      width: '140px',
      align: 'right',
      render: (row) => (
        <span className={row.totalAmountPayable >= 0 ? styles.positiveAmount : styles.negativeAmount}>
          {formatCurrency(row.totalAmountPayable)}
        </span>
      )
    },
    {
      key: 'uploadedBy',
      header: t('courierInvoice.uploadedBy'),
      width: '140px',
      render: (row) => row.uploadedBy?.name || t('common.na')
    },
    {
      key: 'createdAt',
      header: t('courierInvoice.uploadedOn'),
      sortable: true,
      width: '120px',
      render: (row) => formatDate(row.createdAt)
    }
  ];

  // Define table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionButtons}>
        <button
          onClick={() => navigate(`/courier-invoices/${row.id}`)}
          className={styles.actionButton}
          title={t('common.viewDetails')}
        >
          <Eye size={16} />
        </button>

        {canReprocess(row) && hasPermission('reprocess_courier_invoice') && (
          <button
            onClick={() => navigate(`/courier-invoices/${row.id}/reprocess`)}
            className={styles.actionButton}
            title={t('courierInvoice.reprocessFailedOrders')}
          >
            <RefreshCw size={16} />
          </button>
        )}

        {canCancel(row) && hasPermission('create_courier_invoice') && (
          <button
            onClick={() => setConfirmModal({
              show: true,
              type: 'cancel',
              invoiceId: row.id,
              invoiceNumber: row.invoiceNumber
            })}
            className={`${styles.actionButton} ${styles.warningAction}`}
            title={t('courierInvoice.cancelInvoice')}
          >
            <X size={16} />
          </button>
        )}

        {canDelete(row) && hasPermission('delete_courier_invoice') && (
          <button
            onClick={() => setConfirmModal({
              show: true,
              type: 'delete',
              invoiceId: row.id,
              invoiceNumber: row.invoiceNumber
            })}
            className={`${styles.actionButton} ${styles.dangerAction}`}
            title={t('courierInvoice.deleteInvoice')}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    ),
    width: '150px',
    frozen: true
  };

  return (
    <div className={styles.listPage}>
      {/* Page Header */}
      <PageHeader
        title={t('courierInvoice.allInvoices')}
        description={t('courierInvoice.manageAndTrackInvoices')}
        icon={FileText}
        actions={
          hasPermission('create_courier_invoice') && (
            <button
              onClick={() => navigate('/courier-invoices/create')}
              className={styles.primaryButton}
            >
              <Plus size={18} />
              {t('courierInvoice.uploadInvoice')}
            </button>
          )
        }
      />

      {/* Bulk Actions Bar */}
      {selectedInvoices.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <span className={styles.bulkActionsText}>
            {t('common.selectedCount', { count: selectedInvoices.length })}
          </span>
          <button
            onClick={handleBulkDelete}
            className={styles.bulkActionButton}
          >
            <Trash2 size={18} />
            {t('courierInvoice.deleteSelected', { count: selectedInvoices.length })}
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('courierInvoice.searchByInvoice')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      {/* Data Table */}
      <DataTable
        data={invoices}
        columns={columns}
        actions={tableActions}
        selectable
        selectedRows={selectedInvoices}
        onSelectionChange={setSelectedInvoices}
        onRowClick={(row) => navigate(`/courier-invoices/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          Object.values(filters).some(v => v !== '')
            ? t('courierInvoice.noInvoicesFound')
            : t('courierInvoice.noInvoicesYet')
        }
        emptyDescription={
          Object.values(filters).some(v => v !== '')
            ? t('courierInvoice.noInvoicesFoundDescription')
            : t('courierInvoice.uploadFirstInvoiceDescription')
        }
        emptyIcon={<FileText size={64} />}
        emptyAction={
          Object.values(filters).some(v => v !== '') ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_courier_invoice') ? (
            <button
              onClick={() => navigate('/courier-invoices/create')}
              className={styles.primaryButton}
            >
              <Plus size={18} />
              {t('courierInvoice.uploadFirstInvoice')}
            </button>
          ) : null
        }
      />

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className={styles.modalOverlay} onClick={() => setConfirmModal({ show: false, type: '', invoiceId: null, invoiceNumber: '' })}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <AlertCircle size={24} className={styles.modalIcon} />
              <h3>
                {confirmModal.type === 'cancel'
                  ? t('courierInvoice.cancelInvoiceTitle')
                  : t('courierInvoice.deleteInvoiceTitle')}
              </h3>
            </div>
            <div className={styles.modalContent}>
              <p>
                {confirmModal.type === 'cancel'
                  ? t('courierInvoice.cancelInvoiceConfirm')
                  : t('courierInvoice.deleteInvoiceConfirm')}
              </p>
              <p className={styles.invoiceNumberText}>
                <strong>{confirmModal.invoiceNumber}</strong>
              </p>
              {confirmModal.type === 'cancel' && (
                <p className={styles.warningText}>
                  {t('courierInvoice.cancelWarning')}
                </p>
              )}
              {confirmModal.type === 'delete' && (
                <p className={styles.warningText}>
                  {t('courierInvoice.deleteWarning')}
                </p>
              )}
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={() => setConfirmModal({ show: false, type: '', invoiceId: null, invoiceNumber: '' })}
                className={styles.secondaryButton}
              >
                {t('courierInvoice.cancelButton')}
              </button>
              <button
                onClick={() => {
                  if (confirmModal.type === 'cancel') {
                    handleCancelInvoice(confirmModal.invoiceId);
                  } else {
                    handleDeleteInvoice(confirmModal.invoiceId);
                  }
                }}
                className={styles.dangerButton}
              >
                {confirmModal.type === 'cancel'
                  ? t('courierInvoice.confirmCancel')
                  : t('courierInvoice.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourierInvoiceListPage;